import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminSessions, type SessionSort } from "@/features/admin";
import { formatDuration } from "@/features/utils/numbers";
import { ArrowUpIcon, ArrowDownIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { AdminPageLayout } from "../layout";
import { PeriodSelect } from "./components/period-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SORTS: { value: SessionSort; label: string }[] = [
  { value: "silence", label: "Temps mort" },
  { value: "response_delay", label: "Délai de réponse" },
  { value: "fallback_used", label: "Fallback utilisé" },
  { value: "tokens", label: "Tokens" },
  { value: "duration", label: "Durée" },
  { value: "created_at", label: "Date" },
];

const ms = (n: number | null) =>
  n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${Math.round(n)} ms`;
const pct = (n: number | null) => (n == null ? "—" : `${(n * 100).toFixed(1)} %`);

/** Cross-tenant session list with metrics; each row links to the conversation. */
export function AdminSessionsPage() {
  const [days, setDays] = useState(30);
  const [sort, setSort] = useState<SessionSort>("silence");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [fallbackOnly, setFallbackOnly] = useState(false);

  const { data = [], isLoading } = useAdminSessions({
    days,
    sort,
    order,
    fallbackUsed: fallbackOnly,
    limit: 200,
  });

  return (
    <AdminPageLayout
      title="Sessions"
      description="Ouvrez une conversation pour vérifier les messages échangés"
      actions={[<PeriodSelect days={days} onChange={setDays} />]}
    >

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Trier par</span>
        <Select value={sort} onValueChange={(v) => setSort(v as SessionSort)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setOrder((o) => (o === "desc" ? "asc" : "desc"))}
          title={order === "desc" ? "Décroissant" : "Croissant"}
        >
          {order === "desc" ? (
            <ArrowDownIcon className="h-4 w-4" />
          ) : (
            <ArrowUpIcon className="h-4 w-4" />
          )}
        </Button>
        <Button
          variant={fallbackOnly ? "default" : "outline"}
          onClick={() => setFallbackOnly((v) => !v)}
        >
          Fallback utilisé
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : data.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              Aucune session avec métriques sur la période.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Session</TableHead>
                    <TableHead className="text-right">Temps mort</TableHead>
                    <TableHead className="text-right">Délai (p50)</TableHead>
                    <TableHead className="text-right">Fallback</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Durée</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((s) => (
                    <TableRow key={`${s.client_id}-${s.id}`}>
                      <TableCell>{s.client_name || s.client_id}</TableCell>
                      <TableCell>
                        <div className="font-medium">{s.name || "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(s.created_at).toLocaleString("fr-FR")}
                        </div>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {pct(s.silence_ratio)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {ms(s.response_delay_p50)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {s.fallback_used > 0 ? (
                          <span className="text-red-600 dark:text-red-400">
                            {s.fallback_used}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {s.tokens_total ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatDuration((s.duration_ms || 0) / 1000)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/admin/clients/${s.client_id}/calls/session/${s.id}`}
                          className="text-primary hover:underline whitespace-nowrap"
                        >
                          Voir la conversation
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
